/**
 * Liste des dossiers : référence, type de demande, statut, technicien
 * affecté, créé le. Filtre par statut. Inclut le bouton d'export CSV.
 */
import type { Project, ProjectStatus, RequestType } from '@bcl/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import DOMPurify from 'dompurify';
import { Download, FileText, MapPin } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DashboardLayout } from '@/components/dashboard/dashboard-layout';
import {
  getReportFileUrl,
  listAllProjects,
  listClients,
  listReportsForProject,
  listTechnicians,
  updateProjectStatus,
} from '@/features/admin/admin-api';
import { listPartners } from '@/features/partners/partners-api';
import { extractErrorMessage } from '@/features/auth/error-message';
import { requestTypeLabels } from '@/features/projects/labels';
import {
  listAllAppointments,
  type EnrichedAppointment,
} from '@/features/appointments/appointments-api';
import { formatSlot } from '@/features/appointments/appointment-labels';
import { getStatusColor } from '@/features/dashboard/status-color';
import { statusLabels } from '@/features/projects/labels';

// Le kanban (« canvas ») ne démarre qu'à partir d'un dossier complet et payé :
// les étapes client (compte, projet, qualification, documents, validation) sont
// dans le tunnel client, pas dans le back-office.
const POST_PAYMENT: ReadonlySet<ProjectStatus> = new Set([
  'paiement_valide',
  'pris_en_charge',
  'analyse_en_cours',
  'previsite_planifiee',
  'intervention_realisee',
  'compte_rendu_disponible',
  'travaux_en_cours',
  'termine',
]);

// Colonnes du kanban : le cycle de traitement BCL après paiement, piloté par le
// rendez-vous. Un dossier avance automatiquement de colonne au fil du workflow.
const BOARD_COLUMNS = [
  { key: 'a_consulter', label: 'À consulter', cls: 'bg-slate-100 text-slate-800' },
  { key: 'creneaux_proposes', label: 'Créneaux proposés', cls: 'bg-blue-100 text-blue-800' },
  { key: 'a_affecter', label: 'À affecter', cls: 'bg-amber-100 text-amber-800' },
  { key: 'attente_technicien', label: 'Attente technicien', cls: 'bg-purple-100 text-purple-800' },
  { key: 'planifie', label: 'Intervention planifiée', cls: 'bg-emerald-100 text-emerald-800' },
  { key: 'termine', label: 'Terminé', cls: 'bg-green-100 text-green-800' },
] as const;

function boardColumn(
  project: Project,
  appt: EnrichedAppointment | undefined,
): string {
  if (project.status === 'termine') return 'termine';
  switch (appt?.status) {
    case 'creneaux_proposes':
      return 'creneaux_proposes';
    case 'confirme':
    case 'refuse':
      return 'a_affecter';
    case 'attribue':
      return 'attente_technicien';
    case 'accepte':
      return 'planifie';
    case 'realise':
    case 'non_effectue':
      return 'termine';
    default:
      return 'a_consulter';
  }
}

// Couleur par type de demande : repère visuel immédiat.
const REQUEST_TYPE_COLOR: Record<RequestType, string> = {
  etude_raccordement: 'bg-blue-100 text-blue-800',
  point_raccordement: 'bg-cyan-100 text-cyan-800',
  travaux_fibre: 'bg-indigo-100 text-indigo-800',
  previsite_technique: 'bg-amber-100 text-amber-800',
  reparation_sav: 'bg-red-100 text-red-800',
  devis_travaux_divers: 'bg-purple-100 text-purple-800',
  securisation_fibre: 'bg-teal-100 text-teal-800',
  enfouissement_cables: 'bg-lime-100 text-lime-800',
};

function cityOf(p: Project): string | null {
  return p.info?.address?.city ?? null;
}

export function AdminProjectsPage() {
  const [filter, setFilter] = useState<ProjectStatus | ''>('');
  const [sortBy, setSortBy] = useState<'updated' | 'created'>('updated');
  const clients = useQuery({ queryKey: ['admin', 'clients'], queryFn: listClients });
  const clientById = new Map(
    (clients.data ?? []).map((c) => [
      c.id,
      `${c.firstName} ${c.lastName}`.trim(),
    ]),
  );
  const [onlyToAssign, setOnlyToAssign] = useState(false);
  const projects = useQuery<Project[]>({
    queryKey: ['admin', 'projects', filter],
    queryFn: () => listAllProjects(filter || undefined),
  });
  const appointments = useQuery({
    queryKey: ['admin', 'appointments', 'board'],
    queryFn: () => listAllAppointments(),
  });
  const apptByProject = new Map(
    (appointments.data ?? []).map((a) => [a.projectId, a]),
  );
  const technicians = useQuery({ queryKey: ['admin', 'technicians'], queryFn: listTechnicians });
  const partners = useQuery({ queryKey: ['admin', 'partners', 'board'], queryFn: listPartners });
  const assigneeById = new Map<string, string>([
    ...(technicians.data ?? []).map(
      (t) => [t.id, `${t.firstName} ${t.lastName}`.trim()] as const,
    ),
    ...(partners.data ?? []).map(
      (p) =>
        [p.id, p.profile?.companyName ?? `${p.firstName} ${p.lastName}`.trim()] as const,
    ),
  ]);
  // Dossiers payes mais pas encore affectes a un technicien : c'est la file
  // d'action principale du super_admin / admin (point souleve par le user).
  const allProjects = projects.data ?? [];
  const toAssignCount = allProjects.filter(
    (p) => p.status === 'paiement_valide' && !p.assignedTechnicianId,
  ).length;
  const visibleProjects = onlyToAssign
    ? allProjects.filter(
        (p) => p.status === 'paiement_valide' && !p.assignedTechnicianId,
      )
    : allProjects;
  const sortedProjects = [...visibleProjects].sort((a, b) => {
    const ka = sortBy === 'updated' ? a.updatedAt : a.createdAt;
    const kb = sortBy === 'updated' ? b.updatedAt : b.createdAt;
    return new Date(kb).getTime() - new Date(ka).getTime();
  });

  return (
    <DashboardLayout variant="admin" title="Dossiers">
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-3">
          <div>
            <CardTitle>Dossiers</CardTitle>
            <CardDescription>
              {projects.data
                ? `${visibleProjects.length} dossier${visibleProjects.length > 1 ? 's' : ''}${onlyToAssign ? ' à affecter' : ''}`
                : 'Chargement…'}
            </CardDescription>
          </div>
          <Button asChild variant="outline" size="sm">
            <a href="/api/admin/export/projects.csv" target="_blank" rel="noopener">
              <Download className="mr-2 h-4 w-4" />
              Export CSV
            </a>
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <label htmlFor="status" className="text-sm">
              Statut :
            </label>
            <select
              id="status"
              value={filter}
              onChange={(e) => setFilter(e.target.value as ProjectStatus | '')}
              className="h-9 rounded-md border border-input bg-background px-2 text-sm"
            >
              <option value="">Tous</option>
              {(Object.keys(statusLabels) as ProjectStatus[]).map((k) => (
                <option key={k} value={k}>
                  {statusLabels[k]}
                </option>
              ))}
            </select>
            <Button
              variant={onlyToAssign ? 'default' : 'outline'}
              size="sm"
              onClick={() => setOnlyToAssign((v) => !v)}
            >
              À affecter à un technicien
              {toAssignCount > 0 && (
                <Badge variant="secondary" className="ml-2">
                  {toAssignCount}
                </Badge>
              )}
            </Button>
          </div>

          {/* Kanban : dossiers payés, par étape de traitement BCL. */}
          <KanbanBoard
            projects={visibleProjects.filter((p) => POST_PAYMENT.has(p.status))}
            apptByProject={apptByProject}
            clientById={clientById}
            assigneeById={assigneeById}
          />

          {/* Tableau complet, trié par dernière action ou création. */}
          <div className="space-y-3 border-t pt-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-sm font-semibold">Tous les dossiers</h3>
              <div className="flex items-center gap-2 text-sm">
                <label htmlFor="sort">Trier par :</label>
                <select
                  id="sort"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as 'updated' | 'created')}
                  className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                >
                  <option value="updated">Dernière action</option>
                  <option value="created">Date de création</option>
                </select>
              </div>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Référence</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Lieu</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Créé le</TableHead>
                  <TableHead>Dernière action</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedProjects.length > 0 ? (
                  sortedProjects.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell className="font-medium">{p.reference}</TableCell>
                      <TableCell>{clientById.get(p.userId) ?? '—'}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={REQUEST_TYPE_COLOR[p.requestType]}>
                          {requestTypeLabels[p.requestType]}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-gray-500">
                        {cityOf(p) ?? '—'}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={getStatusColor(statusLabels[p.status])}>
                          {statusLabels[p.status]}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-gray-500">
                        {new Date(p.createdAt).toLocaleDateString('fr-FR')}
                      </TableCell>
                      <TableCell className="text-sm text-gray-500">
                        {new Date(p.updatedAt).toLocaleDateString('fr-FR')}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button asChild size="sm" variant="outline">
                          <Link to={`/admin/projects/${p.id}`}>Voir détails</Link>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-sm text-gray-500">
                      Aucun dossier.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </DashboardLayout>
  );
}

/**
 * Kanban du traitement BCL : une colonne par étape (à consulter → créneaux
 * proposés → à affecter → attente technicien → planifié → terminé). La colonne
 * d'un dossier est déduite de l'état de son rendez-vous : il avance donc tout
 * seul au fil du workflow. Chaque carte mène à la fiche dossier.
 */
function KanbanBoard({
  projects,
  apptByProject,
  clientById,
  assigneeById,
}: {
  projects: Project[];
  apptByProject: Map<string, EnrichedAppointment>;
  clientById: Map<string, string>;
  assigneeById: Map<string, string>;
}) {
  const qc = useQueryClient();
  const [closeFor, setCloseFor] = useState<Project | null>(null);

  return (
    <div className="flex gap-3 overflow-x-auto pb-3">
      {BOARD_COLUMNS.map((col) => {
        const items = projects.filter(
          (p) => boardColumn(p, apptByProject.get(p.id)) === col.key,
        );
        return (
          <div key={col.key} className="w-64 shrink-0">
            <div className="mb-2 flex items-center justify-between">
              <Badge variant="outline" className={col.cls}>
                {col.label}
              </Badge>
              <span className="text-xs text-gray-500">{items.length}</span>
            </div>
            <div className="space-y-2">
              {items.map((p) => {
                const appt = apptByProject.get(p.id);
                const city = cityOf(p);
                const info = (
                  <>
                    <div className="font-semibold">
                      {clientById.get(p.userId) ?? 'Client'}
                    </div>
                    <Badge
                      variant="outline"
                      className={REQUEST_TYPE_COLOR[p.requestType]}
                    >
                      {requestTypeLabels[p.requestType]}
                    </Badge>
                    <div className="text-xs text-gray-500">{p.reference}</div>
                    {city && (
                      <div className="flex items-center gap-1 text-xs text-gray-500">
                        <MapPin className="h-3 w-3" />
                        {city}
                      </div>
                    )}
                    {/* Détail enrichi selon l'étape du rendez-vous. */}
                    {appt?.status === 'creneaux_proposes' ? (
                      <div className="text-xs text-blue-700">
                        Proposé :{' '}
                        {appt.proposedSlots.map((s) => formatSlot(s)).join(' · ')}
                      </div>
                    ) : appt?.confirmedSlot ? (
                      <div className="text-xs text-gray-500">
                        {formatSlot(appt.confirmedSlot)}
                        {appt.assigneeId && (
                          <span className="font-medium text-gray-700">
                            {' · '}
                            {assigneeById.get(appt.assigneeId) ?? 'Technicien'}
                          </span>
                        )}
                      </div>
                    ) : (
                      <div className="text-xs text-gray-400">
                        {new Date(p.updatedAt).toLocaleDateString('fr-FR')}
                      </div>
                    )}
                  </>
                );

                // Colonne « Terminé » : la carte garde le lien vers la fiche,
                // mais expose un raccourci pour consulter le rapport et clôturer.
                if (col.key === 'termine') {
                  return (
                    <div
                      key={p.id}
                      className="space-y-2 rounded-md border bg-white p-3 text-sm"
                    >
                      <Link to={`/admin/projects/${p.id}`} className="block space-y-1">
                        {info}
                      </Link>
                      <Button
                        size="sm"
                        variant="outline"
                        className="w-full"
                        onClick={() => setCloseFor(p)}
                      >
                        <FileText className="mr-2 h-4 w-4" />
                        Rapport & clôture
                      </Button>
                    </div>
                  );
                }

                return (
                  <Link
                    key={p.id}
                    to={`/admin/projects/${p.id}`}
                    className="block space-y-1 rounded-md border bg-white p-3 text-sm transition hover:border-gray-400 hover:shadow-sm"
                  >
                    {info}
                  </Link>
                );
              })}
              {items.length === 0 && (
                <p className="rounded-md border border-dashed p-3 text-center text-xs text-gray-300">
                  —
                </p>
              )}
            </div>
          </div>
        );
      })}

      {closeFor && (
        <CloseDossierDialog
          project={closeFor}
          onClose={() => setCloseFor(null)}
          onDone={() => {
            setCloseFor(null);
            qc.invalidateQueries({ queryKey: ['admin', 'projects'] });
          }}
        />
      )}
    </div>
  );
}

/**
 * Consultation du compte rendu + clôture définitive d'un dossier terminé.
 * Affiche la note (HTML assaini) et permet de télécharger le PDF, puis de passer
 * le dossier en « Clôturé » (il quitte le kanban, reste dans la liste).
 */
function CloseDossierDialog({
  project,
  onClose,
  onDone,
}: {
  project: Project;
  onClose: () => void;
  onDone: () => void;
}) {
  const reports = useQuery({
    queryKey: ['admin', 'project', project.id, 'reports'],
    queryFn: () => listReportsForProject(project.id),
  });

  const closeMut = useMutation({
    mutationFn: () =>
      updateProjectStatus(project.id, {
        status: 'cloture',
        message: 'Dossier clôturé',
      }),
    onSuccess: () => {
      toast.success('Dossier clôturé');
      onDone();
    },
    onError: (e) => toast.error(extractErrorMessage(e)),
  });

  async function openFile(reportId: string) {
    try {
      const url = await getReportFileUrl(reportId);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      toast.error(extractErrorMessage(e));
    }
  }

  const items = reports.data ?? [];

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Compte rendu — {project.reference}</DialogTitle>
          <DialogDescription>
            Consultez le compte rendu, puis clôturez le dossier.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[60vh] space-y-3 overflow-y-auto">
          {items.length === 0 ? (
            <p className="text-sm text-gray-500">
              Aucun compte rendu déposé pour ce dossier.
            </p>
          ) : (
            items.map((r) => (
              <div key={r.id} className="rounded-md border bg-white p-3">
                <p className="flex items-center gap-2 font-medium">
                  <FileText className="h-4 w-4" />
                  {r.title}
                </p>
                {r.content && (
                  <div
                    className="mt-1 text-sm text-gray-700 [&_h3]:font-semibold [&_ol]:list-decimal [&_ol]:pl-5 [&_ul]:list-disc [&_ul]:pl-5"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(r.content),
                    }}
                  />
                )}
                {r.fileName && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-2"
                    onClick={() => openFile(r.id)}
                  >
                    <FileText className="mr-2 h-4 w-4" />
                    Télécharger le PDF
                  </Button>
                )}
                <p className="mt-2 text-xs text-gray-500">
                  {new Date(r.createdAt).toLocaleString('fr-FR')}
                </p>
              </div>
            ))
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button
            disabled={closeMut.isPending}
            onClick={() => closeMut.mutate()}
          >
            {closeMut.isPending ? 'Clôture…' : 'Clôturer le dossier'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
